import React, { useEffect, useMemo, useState } from 'react';
import { updateProjectPm } from 'src/features/projects/api';
import { listCompanyUsers, type CompanyUser } from 'src/features/company/api';
import { useToast } from 'src/shared/ui/toast/useToast';

type AssignProjectPmModalProps = {
    open: boolean;
    project: { id: string; name: string } | null;
    onClose: () => void;
    onAssigned?: () => void;
};

const AssignProjectPmModal: React.FC<AssignProjectPmModalProps> = ({ open, project, onClose, onAssigned }) => {
    const toast = useToast();
    
    const [users, setUsers] = useState<CompanyUser[]>([]);
    const [selectedUserId, setSelectedUserId] = useState('');
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    useEffect(() => {
        if (!open) return;

        let mounted = true;

        setErr(null);
        setSelectedUserId('');
        setUsers([]);
        setLoadingUsers(true);

        (async () => {
            try {
                const rows = await listCompanyUsers();
                if (!mounted) return;
                setUsers(Array.isArray(rows) ? rows : []);
            } catch (e: any) {
                if (!mounted) return;
                setErr(e?.response?.data?.error?.message ?? '회사 사용자 목록을 불러오지 못했습니다.');
            } finally {
                if (mounted) setLoadingUsers(false);
            }
        })();

        return () => {
            mounted = false;
        };
    }, [open]);

    const selectableUsers = useMemo(() => {
        return users
            .filter((u) => !!u?.user?.id)
            .sort((a, b) => {
                const an = (a.user.name || '').localeCompare(b.user.name || '');
                if (an !== 0) return an;
                return (a.user.email || '').localeCompare(b.user.email || '');
            });
    }, [users]);

    if (!open || !project) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedUserId) {
            setErr('PM으로 지정할 내부 사용자를 선택해 주세요.');
            return;
        }
        setSaving(true);
        setErr(null);
        
        try {
            await updateProjectPm(project.id, {
                userId: selectedUserId,
            });

            const selectedName = selectedUser?.user?.name || selectedUser?.user?.email || '선택한 사용자';
            // window.alert(`PM이 ${selectedName}로 지정되었습니다.`);
            toast.success(`PM이 ${selectedName}로 지정되었습니다.`, {
              title: '저장/변경 완료',
            });

            setSelectedUserId('');
            if (onAssigned) onAssigned();
            onClose();
        } catch (e: any) {
            setErr(e?.response?.data?.error?.message ?? 'PM을 지정하는 중 문제가 발생했습니다.');
        } finally {
            setSaving(false);
        }
    };

    const selectedUser = selectableUsers.find((u) => u.user.id === selectedUserId) ?? null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5 space-y-4">
                <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-gray-800">프로젝트 PM 지정 / 변경</h3>
                    <button
                        type="button"
                        className="text-xs text-gray-500 hover:text-gray-700"
                        onClick={onClose}
                        disabled={saving}
                    >
                        닫기
                    </button>
                </div>

                <div className="text-xs text-gray-500">
                    <div className="mb-1">
                        <span className="font-semibold text-gray-700">프로젝트:</span> {project.name}
                    </div>
                    <p>
                        아래 목록은 <span className="font-semibold text-gray-700">Users(내부)</span> 에 등록된 회사
                        사용자입니다.
                        <br />이 중 한 명을 선택하여 프로젝트 PM으로 지정하거나 변경할 수 있습니다.
                    </p>
                </div>

                {err && (
                    <div className="text-xs text-rose-600 bg-rose-50 border border-rose-100 rounded-md px-3 py-2">
                        {err}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-3">
                    <div className="space-y-1">
                        <label className="block text-xs font-medium text-gray-700">내부 사용자 선택</label>

                        {loadingUsers ? (
                            <div className="w-full border border-gray-200 rounded-md px-3 py-2 text-xs text-gray-500 bg-gray-50">
                                회사 사용자 목록을 불러오는 중…
                            </div>
                        ) : selectableUsers.length === 0 ? (
                            <div className="w-full border border-amber-200 rounded-md px-3 py-2 text-xs text-amber-700 bg-amber-50">
                                선택 가능한 내부 사용자가 없습니다. 먼저 Users(내부)에서 사용자를 등록해 주세요.
                            </div>
                        ) : (
                            <select
                                className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-sky-500"
                                value={selectedUserId}
                                onChange={(e) => setSelectedUserId(e.target.value)}
                                disabled={saving || loadingUsers}
                            >
                                <option value="">사용자를 선택하세요</option>
                                {selectableUsers.map((u) => (
                                    <option key={u.user.id} value={u.user.id}>
                                        {u.user.name || '(이름 없음)'} ({u.user.email})
                                        {u.role?.name ? ` · ${u.role.name}` : ''}
                                    </option>
                                ))}
                            </select>
                        )}
                    </div>

                    {selectedUser && (
                        <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600">
                            <div>
                                <span className="font-medium text-gray-700">선택 사용자:</span>{' '}
                                {selectedUser.user.name || '(이름 없음)'}
                            </div>
                            <div>
                                <span className="font-medium text-gray-700">이메일:</span> {selectedUser.user.email}
                            </div>
                            <div>
                                <span className="font-medium text-gray-700">회사 역할:</span>{' '}
                                {selectedUser.role?.name ?? '—'}
                            </div>
                        </div>
                    )}

                    <div className="flex justify-end gap-2 pt-2">
                        <button
                            type="button"
                            className="px-3 py-1.5 rounded-md border border-gray-300 text-xs text-gray-700 hover:bg-gray-50"
                            onClick={onClose}
                            disabled={saving}
                        >
                            취소
                        </button>
                        <button
                            type="submit"
                            className="px-3 py-1.5 rounded-md bg-sky-700 text-white text-xs hover:bg-sky-800 disabled:opacity-60"
                            disabled={saving || loadingUsers || selectableUsers.length === 0 || !selectedUserId}
                        >
                            {saving ? '저장 중…' : 'PM 지정'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AssignProjectPmModal;
